/**
 * Response Envelope — Agent-Native MCP Contract (BET-78)
 *
 * Universal response shape for all MCP tool responses.
 * Lives in structuredContent — zero breaking changes to content[0].text.
 *
 * Design: envelope IS structuredContent, not nested inside it.
 * Discriminated on `ok` for O(1) agent parsing.
 */

import { z, type ZodTypeAny } from "zod/v3";
import { classifyError } from "./errors.js";

// ─── Types ───────────────────────────────────────────────────────────────

export interface NextAction {
  tool: string;
  description: string;
  parameters: Record<string, unknown>;
}

/**
 * Describes the schema contract for a single collection's capture tool.
 * Returned in EnvelopeSuccess.contract for write tools so agents can
 * self-describe required fields without a separate round-trip.
 * WP-316 S0 — downstream slices populate this field on capture responses.
 */
export interface CaptureContractField {
  key: string;
  required: boolean;
  type: string;
  helpText?: string;
}

export interface CaptureContract {
  requiredFields: string[];
  allFields: CaptureContractField[];
  captureExpectation?: string;
}

/** Per-step timing for capture pipeline (BET-105, TEN-359). */
export interface CaptureTiming {
  classifyMs: number;
  createMs: number;
  autoLinkMs: number;
  userLinksMs: number;
  qualityMs: number;
  totalMs: number;
}

/** Batch capture timing: wall-clock total and per-entry average. */
export interface BatchCaptureTiming {
  totalMs: number;
  perEntryAvgMs: number;
}

export interface EnvelopeMeta {
  durationMs?: number;
  /** Per-step timing (single capture) or batch timing (capture action=batch). */
  timing?: CaptureTiming | BatchCaptureTiming;
  /**
   * WP-484 S1 (§4): self-correction hint for the handler-layer backpressure
   * channel. Written against param ABSENCE only, never misspelling — the SDK
   * silently strips undeclared/typo'd params before the handler ever sees
   * them, so a typo surfaces as "missing `<param>`," never "did you mean."
   */
  tip?: string;
  /**
   * WP-484 S1 (§4): structured wrong-call backpressure payload — the
   * handler-layer's answer for a call that passed the registered flat
   * schema's action-enum + type checks but violates the per-action
   * discriminated shape (a valid action with a wrong param combination).
   */
  advisory?: Record<string, unknown>;
}

// Declared as type ALIASES, not interfaces, on purpose: the envelope is assigned to
// `ToolResult.structuredContent?: Record<string, unknown>` (:139), and TypeScript grants an
// implicit index signature to an object type alias but NOT to an interface. As interfaces these
// were unassignable — 18 TS2322s that nothing could see, because this package was typechecked by
// nothing until DEC-1447. Keep them as `type`.
export type EnvelopeSuccess<T = Record<string, unknown>> = {
  ok: true;
  summary: string;
  data: T;
  next?: NextAction[];
  /** WP-316 S0: Optional capture contract — populated by write tools on create/update responses. */
  contract?: CaptureContract;
  _meta?: EnvelopeMeta;
};

export type EnvelopeError = {
  ok: false;
  code: string;
  message: string;
  recovery?: string;
  availableActions?: NextAction[];
  diagnostics?: Record<string, unknown>;
  _meta?: EnvelopeMeta;
};

export type Envelope<T = Record<string, unknown>> =
  | EnvelopeSuccess<T>
  | EnvelopeError;

// ─── Builders ────────────────────────────────────────────────────────────

export function success<T>(
  summary: string,
  data: T,
  next?: NextAction[],
): EnvelopeSuccess<T> {
  return {
    ok: true,
    summary: summary || "Operation completed.",
    data,
    ...(next?.length ? { next } : {}),
  };
}

export function failure(
  code: string,
  message: string,
  recovery?: string,
  availableActions?: NextAction[],
  diagnostics?: Record<string, unknown>,
): EnvelopeError {
  return {
    ok: false,
    code,
    message,
    ...(recovery ? { recovery } : {}),
    ...(availableActions?.length ? { availableActions } : {}),
    ...(diagnostics ? { diagnostics } : {}),
  };
}

/** Shorthand for NOT_FOUND failures — the most common soft-failure pattern. */
export function notFound(id: string, hint?: string): EnvelopeError {
  return failure(
    "NOT_FOUND",
    `Entry '${id}' not found.`,
    hint ?? "Use entries action=search to find the correct ID.",
    [{ tool: "entries", description: "Search entries", parameters: { action: "search", query: id } }],
  );
}

/** Shorthand for VALIDATION_ERROR failures. */
export function validationError(message: string): EnvelopeError {
  return failure("VALIDATION_ERROR", message);
}

// ─── Tool Response Helpers ───────────────────────────────────────────────

type TextContent = { type: "text"; text: string };
// `structuredContent` is REQUIRED, not optional. Every builder below sets it, and the envelope
// contract is that every tool response carries one — so `?` was a lie the typechecker could never
// catch (this package was typechecked by nothing until DEC-1447). Left optional, every read of
// `result.structuredContent.*` is "possibly undefined": ~112 TS18048s across the tests, not one of
// them real. The MCP SDK's own wire type keeps it optional; ours is the narrower internal contract,
// and it is assignable to the SDK's.
//
// It is deliberately `Record<string, unknown>` and NOT `Envelope`. That looks like the obvious
// improvement — it would restore discriminated-union narrowing at every call site — and it does not
// work: `successResult<T>` builds an `EnvelopeSuccess<T>` for arbitrary `T`, which is not assignable
// to `Envelope` (= `Envelope<Record<string, unknown>>`) unless `T extends Record<string, unknown>`,
// and plenty of handlers return arrays and scalars. MEASURED: it takes the package from 23 errors to
// 42. Two independent reviewers proposed it; it is wrong. Narrow on the READ side instead — the
// tests use small runtime-checked accessors that assert `ok` and return the right arm.
export type ToolResult = {
  content: TextContent[];
  structuredContent: Record<string, unknown>;
  isError?: boolean;
};

/** Build a `content` array from a single text string. */
export function textContent(text: string): TextContent[] {
  return [{ type: "text" as const, text }];
}

/**
 * Parse args against a Zod schema and return a typed result or a ready-to-return
 * validation error ToolResult. Eliminates the 5-line parse+check+format boilerplate.
 */
export function parseOrFail<T extends z.ZodTypeAny>(
  schema: T,
  args: unknown,
): { ok: true; data: z.infer<T> } | { ok: false; result: ToolResult } {
  const parsed = schema.safeParse(args);
  if (parsed.success) return { ok: true, data: parsed.data };
  const issues = parsed.error.issues
    .map((i: z.ZodIssue) => `${i.path.join(".")}: ${i.message}`)
    .join("; ");
  const msg = `Invalid arguments: ${issues}`;
  return {
    ok: false,
    result: { content: textContent(msg), structuredContent: validationError(msg) },
  };
}

/** Standard response for unknown compound-tool actions. */
export function unknownAction(action: string, valid: readonly string[]): ToolResult {
  const msg = `Unknown action '${action}'. Valid actions: ${valid.join(", ")}.`;
  return { content: textContent(msg), structuredContent: validationError(msg) };
}

// ─── Compound Result Builders ─────────────────────────────────────────
// Eliminate the { content: [...], structuredContent: ... } boilerplate.

/** Success result with human-readable text + structured envelope in one call. */
export function successResult<T>(
  text: string,
  summary: string,
  data: T,
  next?: NextAction[],
): ToolResult {
  return { content: textContent(text), structuredContent: success(summary, data, next) };
}

/** Failure result with human-readable text + structured error envelope. */
export function failureResult(
  text: string,
  code: string,
  message: string,
  recovery?: string,
  availableActions?: NextAction[],
  diagnostics?: Record<string, unknown>,
): ToolResult {
  return { content: textContent(text), structuredContent: failure(code, message, recovery, availableActions, diagnostics) };
}

/** Not-found result — the most common soft failure pattern. */
export function notFoundResult(id: string, text?: string, hint?: string): ToolResult {
  return { content: textContent(text ?? `Entry '${id}' not found.`), structuredContent: notFound(id, hint) };
}

/** Validation error result where the text and error message are the same. */
export function validationResult(message: string): ToolResult {
  return { content: textContent(message), structuredContent: validationError(message) };
}

// ─── Zod Schema Helper ──────────────────────────────────────────────────

const nextActionSchema = z.object({
  tool: z.string(),
  description: z.string(),
  parameters: z.record(z.unknown()),
});

const metaSchema = z
  .object({
    durationMs: z.number().optional(),
    timing: z
      .object({
        classifyMs: z.number().optional(),
        createMs: z.number().optional(),
        autoLinkMs: z.number().optional(),
        userLinksMs: z.number().optional(),
        qualityMs: z.number().optional(),
        totalMs: z.number(),
        perEntryAvgMs: z.number().optional(),
      })
      .passthrough()
      .optional(),
    // WP-484 S1 (§4): backpressure channel — see EnvelopeMeta for the contract.
    tip: z.string().optional(),
    advisory: z.record(z.unknown()).optional(),
  })
  .optional();

/**
 * Wraps any data schema into a discriminated-union envelope schema.
 * Use for contract tests: `envelopeSchema(captureOutputSchema).safeParse(result)`.
 */
export function envelopeSchema<T extends ZodTypeAny>(dataSchema: T) {
  return z.discriminatedUnion("ok", [
    z.object({
      ok: z.literal(true),
      summary: z.string().min(1),
      data: dataSchema,
      next: z.array(nextActionSchema).optional(),
      /** WP-316 S1a: capture contract for write tools — optional for backward compat. */
      contract: z.object({
        requiredFields: z.array(z.string()),
        allFields: z.array(z.object({
          key: z.string(),
          required: z.boolean(),
          type: z.string(),
          helpText: z.string().optional(),
        })),
        captureExpectation: z.string().optional(),
      }).optional(),
      _meta: metaSchema,
    }),
    z.object({
      ok: z.literal(false),
      code: z.string().min(1),
      message: z.string().min(1),
      recovery: z.string().optional(),
      availableActions: z.array(nextActionSchema).optional(),
      diagnostics: z.record(z.unknown()).optional(),
      _meta: metaSchema,
      // DEC-798
      class: z.enum(['transient','permanent','auth','notfound','payload','rate_limited']).optional(),
      retryAdvised: z.boolean().optional(),
    }),
  ]);
}

// ─── withEnvelope HOF ───────────────────────────────────────────────────

/**
 * Wraps a tool handler to guarantee an Envelope in structuredContent.
 *
 * - Catches thrown errors (gates, backend) and classifies them.
 * - If the handler already returns an envelope (ok field present), passes through.
 * - Adds _meta.durationMs to every response.
 * - Owns isError: sets true iff ok===false (RH1 mitigation — TEN-206).
 *
 * @deprecated Use `thinWrapper` for new and migrated tools. `withEnvelope` retains
 * the safety net that auto-wraps non-envelope responses; `thinWrapper` throws instead,
 * making incomplete migrations visible immediately.
 */
export function withEnvelope<Args>(
  handler: (args: Args) => Promise<ToolResult>,
): (args: Args) => Promise<ToolResult> {
  return async (args: Args): Promise<ToolResult> => {
    const start = Date.now();
    try {
      const result = await handler(args);
      const durationMs = Date.now() - start;

      const sc = result.structuredContent as Envelope | undefined;
      if (sc && typeof sc === "object" && "ok" in sc) {
        sc._meta = { ...sc._meta, durationMs };
        return {
          content: result.content ?? [],
          structuredContent: sc,
          ...(sc.ok === false ? { isError: true } : {}),
        };
      }

      // Safety net: all tools should be migrated. If this executes, a handler
      // is returning without an envelope — log so it surfaces during development.
      console.warn(`[withEnvelope] Handler returned without envelope shape. Wrapping automatically. Content preview: "${result.content?.[0]?.text?.slice(0, 60) ?? "(empty)"}"`);
      return {
        content: result.content ?? [],
        structuredContent: {
          ...success(
            result.content?.[0]?.text?.slice(0, 100) ?? "",
            sc ?? {},
          ),
          _meta: { durationMs },
        },
      };
    } catch (err) {
      const durationMs = Date.now() - start;
      const classified = classifyError(err);
      const envelope: EnvelopeError = {
        ...failure(
          classified.code,
          classified.message,
          classified.recovery,
          classified.availableActions,
          // WP-316 S1a: pass structured diagnostics (e.g. missingRequiredFields, fieldErrors)
          classified.diagnostics as Record<string, unknown> | undefined,
        ),
        _meta: { durationMs },
      };
      return {
        content: [{
          type: "text" as const,
          text: classified.fn
            ? `[${classified.code}/${classified.fn}] ${classified.message}`
            : `[${classified.code}] ${classified.message}`
        }],
        structuredContent: envelope,
        isError: true,
      };
    }
  };
}

// ─── thinWrapper HOF ────────────────────────────────────────────────────

/**
 * Strict variant of `withEnvelope` for fully-migrated tools (WP-321 E3).
 *
 * Identical to `withEnvelope` EXCEPT the safety net is replaced with a hard
 * throw. If a handler returns without an envelope shape, this surfaces
 * immediately as an error rather than silently wrapping the response.
 *
 * Use this for all new tools and all tools that have been migrated to return
 * envelope-shaped responses directly.
 */
export function thinWrapper<Args>(
  handler: (args: Args) => Promise<ToolResult>,
): (args: Args) => Promise<ToolResult> {
  return async (args: Args): Promise<ToolResult> => {
    const start = Date.now();
    try {
      const result = await handler(args);
      const durationMs = Date.now() - start;

      const sc = result.structuredContent as Envelope | undefined;
      if (sc && typeof sc === "object" && "ok" in sc) {
        sc._meta = { ...sc._meta, durationMs };
        return {
          content: result.content ?? [],
          structuredContent: sc,
          ...(sc.ok === false ? { isError: true } : {}),
        };
      }

      throw new Error(`[thinWrapper] Handler '${handler.name || "<anonymous>"}' returned without envelope shape — migration incomplete.`);
    } catch (err) {
      const durationMs = Date.now() - start;
      const classified = classifyError(err);
      const envelope: EnvelopeError = {
        ...failure(
          classified.code,
          classified.message,
          classified.recovery,
          classified.availableActions,
          // WP-316 S1a: pass structured diagnostics (e.g. missingRequiredFields, fieldErrors)
          classified.diagnostics as Record<string, unknown> | undefined,
        ),
        _meta: { durationMs },
      };
      return {
        content: [{
          type: "text" as const,
          text: classified.fn
            ? `[${classified.code}/${classified.fn}] ${classified.message}`
            : `[${classified.code}] ${classified.message}`
        }],
        structuredContent: envelope,
        isError: true,
      };
    }
  };
}
